import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { fetchNotifications, markAllNotificationsRead } from '../api/notifications';
import { unreadCountAtom } from '../atoms/notificationAtoms';
import { formatTimeAgo } from '../helpers/timeHelper';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const PAGE_SIZE = 20;

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const setUnreadCount = useSetAtom(unreadCountAtom);
    const sentinelRef = useRef(null);
    const loadingRef = useRef(false);

    const loadPage = useCallback(async (cursor) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        const isFirst = !cursor;
        if (isFirst) setLoading(true); else setLoadingMore(true);

        try {
            const data = await fetchNotifications({ cursor, limit: PAGE_SIZE });
            setHasMore(data.hasMore);
            setNotifications(prev => isFirst ? data.notifications : [...prev, ...data.notifications]);
        } catch {
            if (isFirst) toast.error('Error loading notifications');
        } finally {
            loadingRef.current = false;
            if (isFirst) setLoading(false); else setLoadingMore(false);
        }
    }, []);

    useEffect(() => { loadPage(null); }, [loadPage]);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) return;

        const lastID = notifications.length > 0
            ? notifications[notifications.length - 1].notificationID
            : null;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && !loadingRef.current) {
                    loadPage(lastID);
                }
            },
            { rootMargin: '400px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, notifications, loadPage]);

    const handleMarkAllRead = useCallback(async () => {
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
            setUnreadCount(0);
        } catch {
            toast.error('Failed to mark notifications as read');
        }
    }, [setUnreadCount]);

    if (loading) return <LoadingSpinner />;

    const hasUnread = notifications.some(n => !n.readAt);

    return (
        <div className="notifications-page">
            <div className="notifications-page-header">
                <h2>Notifications</h2>
                {hasUnread ? (
                    <button className="notifications-mark-all-btn" onClick={handleMarkAllRead}>
                        Mark all as read
                    </button>
                ) : null}
            </div>

            {notifications.length === 0 ? (
                <p>No notifications yet.</p>
            ) : (
                <div className="notifications-list">
                    {notifications.map(n => (
                        <div key={n.notificationID} className={`notifications-page-item ${n.readAt ? 'read' : 'unread'}`}>
                            {n.link ? (
                                <Link to={n.link}>
                                    <span className="notifications-page-message">{n.message}</span>
                                    <span className="notifications-page-time">{formatTimeAgo(n.created)}</span>
                                </Link>
                            ) : (
                                <>
                                    <span className="notifications-page-message">{n.message}</span>
                                    <span className="notifications-page-time">{formatTimeAgo(n.created)}</span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div ref={sentinelRef} className="notifications-loader">
                {loadingMore ? 'Loading...' : null}
            </div>

            {!hasMore && notifications.length > 0 ? (
                <div className="notifications-end">All notifications loaded</div>
            ) : null}
        </div>
    );
};

export default NotificationsPage;
